import type AreaAnalysis from "../AreaAnalysis"
import type Area from "../Area"
import type AnalysisBuilder from "./AnalysisBuilder"

const GLOBAL_MEDIAN_BASELINE = 425_000
/** Band around baseline (e.g. ±10%) treated as "in line with" average. */
const AVERAGE_BAND_FRACTION = 0.1
const AVERAGE_LOW = GLOBAL_MEDIAN_BASELINE * (1 - AVERAGE_BAND_FRACTION)
const AVERAGE_HIGH = GLOBAL_MEDIAN_BASELINE * (1 + AVERAGE_BAND_FRACTION)

function medianValue(analysis: AreaAnalysis): number | null {
	const m = analysis.one_year_median_price
	if (!m || m.v == null) return null
	return typeof m.v === "number" ? m.v : parseFloat(String(m.v))
}

export class MedianPriceAnalysisBuilder implements AnalysisBuilder {
	build(analysis: AreaAnalysis, area: Area): string {
		const area_name = area.getDisplayName()
		const median = medianValue(analysis)
		if (median == null) return ""

		if (median >= AVERAGE_LOW && median <= AVERAGE_HIGH) {
			return this.average_baseline_statement(area_name, median)
		}
		if (median > GLOBAL_MEDIAN_BASELINE) {
			return this.above_baseline_statement(area_name, median)
		}
		return this.below_baseline_statement(area_name, median)
	}

	private average_baseline_statement(area_name: string, median: number): string {
		return (
			"Median prices in " +
			area_name +
			" are broadly in line with wider market levels, at around €" +
			this.formatPrice(median) +
			". This points to balanced demand and a healthy local market."
		)
	}

	private above_baseline_statement(area_name: string, median: number): string {
		const premiumPercent = Math.round(((median - GLOBAL_MEDIAN_BASELINE) / GLOBAL_MEDIAN_BASELINE) * 100)
		return (
			"Median prices in " +
			area_name +
			" are around €" +
			this.formatPrice(median) +
			", which is roughly " +
			premiumPercent +
			"% above broader market averages. That profile is often associated with stronger demand, tighter supply, and a relatively affluent buyer base."
		)
	}

	private below_baseline_statement(area_name: string, median: number): string {
		const discountPercent = Math.round(((GLOBAL_MEDIAN_BASELINE - median) / GLOBAL_MEDIAN_BASELINE) * 100)
		return (
			"Median prices in " +
			area_name +
			" are around €" +
			this.formatPrice(median) +
			", roughly " +
			discountPercent +
			"% below broader market levels. That can indicate better value-entry opportunities."
		)
	}

	private formatPrice(value: number): string {
		return value >= 1000 ? (value / 1000).toFixed(0) + "K" : String(value)
	}
}
